import React from 'react';

import { Backdrop, Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import ExportPDFButton from 'src/common/components/ExportPDFButton';
import { FlorencePAGES } from 'src/common/components/tfa-auth/constants/pages';
import { useNotes } from 'src/florence/hooks/useNotes';

import { usePrefetch } from '../../../common/apis/FlorenceApi';
import PaginationControl from '../../../common/components/PaginationControl';
import { SaveSheet, SheetStatus } from '../../../common/types/SaveSheet';
import BottomResultsBar from '../../components/BottomResultsBar/BottomResultsBar';
import ConfirmFinalizePopup from '../../components/ConfirmFinalizePopup/ConfirmFinalizePopup';
import FiltersContainer from '../../components/FiltersContainer/FiltersContainer';
import SearchResultsContainer from '../../components/SearchResultsContainer/SearchResultsContainer';
import { useCodes } from '../../hooks/useCodes';
import { SearchResultsParams } from '../../types/RouterState';

import './SearchResultsScreen.scss';

import EditOpNoteExtendedSurgery from '../EditOPNoteScreenV2/EditOpNoteExtendedSurgery';

const SearchResultsScreen: React.FC = () => {
  const { searchText, searchType, notes } = useLocation()
    .state as SearchResultsParams;

  // filters
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [limit, setLimit] = React.useState<10 | 15 | 25 | 10000>(
    searchType === 'scan' ? 10 : searchText.length > 250 ? 10 : 10000
  );

  // ui
  const [isSaving, setIsSaving] = React.useState(false);
  const [finalizeOpen, setFinalizeOpen] = React.useState(false);

  // misc
  const navigate = useNavigate();
  const getModifiers = usePrefetch('getModifiers');

  // note and codes
  const {
    currentNote,
    page,
    finalizeNote,
    updateNote,
    changeClinicNoteStatus,
    updateNoteText,
    setPage,
  } = useNotes(notes ?? []);

  const {
    codes,
    selectedCodes,
    refetchCodes,
    isLoading,
    filtersLength,
    selectModifiers,
    selectMultiplier,
    applyFilters,
    clearFilters,
    clinicResponse,
    selectCode,
    selectAllCodes,
    clearSelectedCodes,
    addManualCode,
    incorrectCodeCombinations,
    flagCode,
    updateICD10,
    suggestedICD10,
    AIOutput,
  } = useCodes(
    currentNote,
    searchType === 'scan' ? currentNote?.scanText ?? '' : searchText,
    searchType,
    limit,
    updateNote
  );

  // Run on page load
  React.useEffect(() => {
    if (searchType === 'scan' && currentNote && !currentNote?.clinicNote) {
      // for OP Notes with the extended engine don't refetch codes as we're doing that inside their own component
      getModifiers();
    } else {
      refetchCodes();
      getModifiers();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedAll = (select: boolean) => {
    if (select) selectAllCodes();
    else clearSelectedCodes();
  };

  const onSaveNote = async (showConfirmationMessage = false) => {
    if (searchType !== 'scan') return;

    const sheetToSave = {
      ...currentNote,
      sheetCodes: selectedCodes,
      clinicNoteResults: clinicResponse,
      aiOutput: AIOutput,
    };
    if (!sheetToSave) return;

    setIsSaving(true);
    await updateNote(sheetToSave as SaveSheet, showConfirmationMessage);
    setIsSaving(false);
  };

  const updateClinicNoteStatus = async (status: string) => {
    if (!currentNote) return;
    setIsSaving(true);

    clearSelectedCodes();
    await changeClinicNoteStatus(currentNote, status);
    setIsSaving(false);

    refetchCodes();
  };

  const onFinalizeSheet = async () => {
    if (currentNote?.clinicNote) return;
    if (searchType !== 'scan') return;

    setIsSaving(true);
    setFinalizeOpen(false);

    const opNote = {
      ...currentNote,
      sheetCodes: selectedCodes,
      aiOutput: AIOutput,
    };
    if (!opNote) return;

    // Save the note first in case the user selected more codes before saving

    setIsSaving(true);
    setFinalizeOpen(false);
    await updateNote(opNote as SaveSheet, false);
    await finalizeNote(opNote as SaveSheet);
    setIsSaving(false);
  };

  return (
    <>
      {searchType === 'scan' && currentNote && !currentNote?.clinicNote ? (
        <EditOpNoteExtendedSurgery notes={notes ?? []} />
      ) : (
        <>
          <Box className='results-content-wrapper responsive-padding light-blue-background'>
            <Backdrop
              sx={{
                color: '#fff',
                zIndex: 99,
                justifyContent: 'center',
                alignItems: 'center',
              }}
              open={filtersOpen || finalizeOpen}
            >
              {filtersOpen && (
                <FiltersContainer
                  onClickApply={applyFilters}
                  onClickClose={() => setFiltersOpen(false)}
                  onClickCancelAll={clearFilters}
                />
              )}
              {finalizeOpen && (
                <ConfirmFinalizePopup
                  onFinalize={onFinalizeSheet}
                  onClickClose={() => {
                    setFinalizeOpen(false);
                  }}
                />
              )}
            </Backdrop>
            {searchType === 'scan' && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginLeft: '4px',
                }}
              >
                <Typography
                  fontWeight={600}
                  fontSize={16}
                  color='darkBlue'
                  sx={{ marginTop: '8px', marginBottom: '4px' }}
                >
                  {currentNote?.clinicNote ? 'Clinic Note' : 'OP Note'}
                </Typography>
                {currentNote?.name && (
                  <Typography fontFamily='SFProDisplay-Bold'>
                    File name:{' '}
                    <span style={{ fontFamily: 'SFProDisplay-Medium' }}>
                      {currentNote.name}
                    </span>
                  </Typography>
                )}
              </Box>
            )}
            <Box
              className='results-content-header'
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginLeft: 'auto',
                }}
              >
                {currentNote &&
                  (currentNote.clinicNote ||
                    currentNote.status === SheetStatus.FINALIZED) && (
                    <ExportPDFButton
                      isDisabled={isLoading}
                      note={currentNote}
                    />
                  )}
                {searchType === 'scan' && notes && notes.length > 1 && (
                  <PaginationControl
                    sx={{
                      '& ul > li:not(:first-of-type):not(:last-of-type)': {
                        display: 'none',
                      },
                    }}
                    count={notes?.length ?? 0}
                    page={page + 1}
                    onChange={(e, p) => setPage(p - 1)}
                    disabled={isLoading}
                  />
                )}
              </Box>
            </Box>
            <SearchResultsContainer
              hasSelectedAll={
                codes.length !== 0 &&
                codes.filter((c) => !c.selected).length === 0
              }
              selectedAll={selectedAll}
              selectCode={selectCode}
              selectMultiplier={selectMultiplier}
              selectModifiers={selectModifiers}
              limit={limit}
              changeLimit={setLimit}
              searchResults={codes}
              currentNote={currentNote}
              clinicResponse={clinicResponse}
              searchType={searchType}
              searchText={
                searchType === 'scan' ? currentNote?.scanText ?? '' : searchText
              }
              refreshTextSearch={(text) => {
                if (currentNote) {
                  updateNoteText(currentNote, text);
                } else {
                  navigate(FlorencePAGES.results, {
                    state: {
                      searchText: text.trim(),
                      searchType: 'text',
                    } as SearchResultsParams,
                  });
                }
              }}
              refetchCodes={refetchCodes}
              selectedFilterIcon={() => setFiltersOpen(!filtersOpen)}
              filtersLength={filtersLength}
              isLoading={isLoading || isSaving}
              updateClinicNoteStatus={updateClinicNoteStatus}
              clearSelectedCodes={clearSelectedCodes}
              flagCode={flagCode}
              updateNote={updateNote}
              allowNoteTextEditing={true}
              addManualCode={addManualCode}
              updateICD10={updateICD10}
              suggestedICD10={suggestedICD10}
              selectedCodes={selectedCodes}
            />
          </Box>
          <BottomResultsBar
            open={
              (selectedCodes.length !== 0 &&
                (currentNote?.status === SheetStatus.DRAFT ||
                  currentNote?.status === SheetStatus.UPLOADED)) ||
              clinicResponse?.code_details !== null
            }
            searchType={searchType}
            onSaveSheet={() => onSaveNote(true)}
            onClickFinalize={() => setFinalizeOpen(true)}
            selectedCodes={selectedCodes}
            disabled={isSaving || isLoading}
            incorrectCodeCombinations={incorrectCodeCombinations}
            isClinicNote={currentNote?.clinicNote}
          />
        </>
      )}
    </>
  );
};

export default SearchResultsScreen;
